import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useLocation } from "wouter";
import { useClerk, useUser } from "@clerk/clerk-react";
import { Layout } from "@/components/layout";
import { OrganizationInfoCard } from "@/components/organizations/OrganizationInfoCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingCart, Clock, Users, Zap } from "lucide-react";

const STEP_TITLES = ["Welcome", "Invite Team Members", "Start an Order"];
import { StepIndicator } from "@/components/onboarding/StepIndicator";

export default function ContractorOnboardingWelcome() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const clerk = useClerk();
  const { user, isLoaded } = useUser();

  // Mark user as onboarded when they visit this page
  useEffect(() => {
    const markOnboarded = async () => {
      if (!isLoaded || !user || !clerk.user) return;

      // Check if already onboarded to avoid unnecessary updates
      const onboardedTimestamp = user.unsafeMetadata?.onboarded as string;

      if (onboardedTimestamp) {
        return; // Already onboarded
      }

      try {
        // Update unsafeMetadata with onboarding timestamp
        await clerk.user.update({
          unsafeMetadata: {
            onboarded: new Date().toISOString(),
          },
        });
      } catch (error) {
        console.error("Failed to update onboarding metadata:", error);
      }
    };

    markOnboarded();
  }, [isLoaded, user, clerk]);

  const handleInviteTeamMembers = () => {
    setLocation("/c-onboarding/invite");
  };

  const handleStartOrder = () => {
    setLocation("/c-onboarding/landing");
  };

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber === 2) {
      setLocation("/c-onboarding/invite");
    } else if (stepNumber === 3) {
      setLocation("/c-onboarding/landing");
    }
  };

  return (
    <Layout hideSidebar>
      <div className="max-w-5xl mx-auto">
        <StepIndicator
          currentStep={1}
          totalSteps={3}
          stepTitles={STEP_TITLES}
          onStepClick={handleStepClick}
        />
        <div className="text-center mb-12 space-y-4">
          <h1 className="text-4xl font-bold">{t("pages.cOnboarding.welcome.welcomeToOnsemblePro")}</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t("pages.cOnboarding.welcome.streamlineYourOrderingProcess")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Value Proposition Card */}
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold">{t("pages.cOnboarding.welcome.orderFaster")}</h2>
                </div>

                <div className="space-y-4 text-muted-foreground">
                  <p className="leading-relaxed">
                    {t("pages.cOnboarding.welcome.onsembleProGivesYou")}
                  </p>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-start gap-3">
                      <ShoppingCart className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">
                          {t("pages.cOnboarding.welcome.247SelfService")}
                        </p>
                        <p className="text-sm">
                          {t("pages.cOnboarding.welcome.browseProductsCheckPricing")}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">{t("pages.cOnboarding.welcome.saveTime")}</p>
                        <p className="text-sm">
                          {t("pages.cOnboarding.welcome.skipTheBackAnd")}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">
                          {t("pages.cOnboarding.welcome.teamCollaboration")}
                        </p>
                        <p className="text-sm">
                          {t("pages.cOnboarding.welcome.inviteYourTeamMembers")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Your Organization Card */}
          <div>
            <OrganizationInfoCard />
          </div>
        </div>

        {/* Call to Action */}
        <Card className="bg-primary/5 border-2 border-primary/20">
          <CardContent className="pt-6">
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold">
                  {t("pages.cOnboarding.welcome.readyToGetStarted")}
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  {t("pages.cOnboarding.welcome.inviteYourTeamMembers_4")}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  onClick={handleInviteTeamMembers}
                  className="min-w-48 h-12 text-lg"
                >
                  {t("pages.cOnboarding.welcome.inviteTeamMembers")}
                </Button>

                <Button
                  size="lg"
                  onClick={handleStartOrder}
                  className="min-w-48 h-12 text-lg"
                >
                  {t("pages.cOnboarding.welcome.startAnOrder")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
